<template>
  <div class="api-doc-layout" :class="{ 'is-mobile': isMobile }">
    <!-- 移动端菜单按钮 -->
    <div v-if="isMobile" class="mobile-menu-bar">
      <div class="mobile-left">
        <el-button type="primary" :icon="Menu" circle @click="drawerVisible = true" />
        <!-- 移动端上传按钮 -->
        <el-button type="success" :icon="Upload" circle @click="openUploadDialog" />
      </div>
      <span class="mobile-title">{{ currentDocTitle || '文档' }}</span>
    </div>

    <!-- 左侧文档树 - 桌面端 -->
    <div v-if="!isMobile" class="doc-tree-container">
      <div class="tree-header">
        <span class="tree-title">文档目录</span>
        <el-button type="success" :icon="Upload" size="small" @click="openUploadDialog">
          上传文档
        </el-button>
      </div>
      <el-tree ref="treeRef" class="doc-tree" :data="menu" node-key="key" :props="treeProps" highlight-current
        :expand-on-click-node="true" @node-click="handleNodeClick">
        <!-- 右键菜单支持 -->
        <template #default="{ data }">
          <span class="tree-node" :title="data.label" @contextmenu.prevent="handleContextMenu($event, data)">
            <span class="tree-label" :class="{
              'is-dir': data.type === 'dir',
              'is-md': data.type === 'md',
              'is-pdf': data.type === 'pdf'
            }">
              {{ data.label }}
            </span>
          </span>
        </template>
      </el-tree>
    </div>

    <!-- 移动端抽屉菜单 -->
    <el-drawer v-if="isMobile" v-model="drawerVisible" direction="ltr" size="80%" :with-header="false"
      class="mobile-drawer">
      <div class="drawer-header">
        <span class="drawer-title">文档目录</span>
        <el-button type="success" :icon="Upload" size="small" @click="openUploadDialog">
          上传
        </el-button>
      </div>
      <el-tree ref="treeRef" class="doc-tree mobile-tree" :data="menu" node-key="key" :props="treeProps"
        highlight-current :expand-on-click-node="true" @node-click="handleMobileNodeClick">
        <template #default="{ data }">
          <span class="tree-node" :title="data.label">
            <span class="tree-label" :class="{
              'is-dir': data.type === 'dir',
              'is-md': data.type === 'md',
              'is-pdf': data.type === 'pdf'
            }">
              {{ data.label }}
            </span>
          </span>
        </template>
      </el-tree>
    </el-drawer>

    <!-- 右键菜单 -->
    <div v-show="contextMenu.visible" class="context-menu" :style="contextMenu.style">
      <div class="context-menu-item" @click="handleRename">
        <el-icon>
          <Edit />
        </el-icon>
        <span>重命名</span>
      </div>
      <div class="context-menu-item" @click="handleMove">
        <el-icon>
          <FolderOpened />
        </el-icon>
        <span>移动到</span>
      </div>
      <div v-if="isManager" class="context-menu-item delete" @click="handleDelete">
        <el-icon>
          <Delete />
        </el-icon>
        <span>删除</span>
      </div>
    </div>

    <!-- 上传文档对话框（改进版） -->
    <el-dialog v-model="uploadDialogVisible" title="上传文档" width="600px" @closed="handleUploadClosed">
      <el-form :model="uploadForm" :rules="uploadRules" ref="uploadFormRef" label-width="100px">
        <!-- 显示自动检测的文档类型 -->
        <el-form-item label="文档类型" v-if="uploadForm.type">
          <el-tag :type="uploadForm.type === 'md' ? 'primary' : 'warning'">
            {{ uploadForm.type === 'md' ? 'Markdown' : 'PDF' }}
          </el-tag>
          <span class="type-tip">（根据文件后缀自动识别）</span>
        </el-form-item>

        <el-form-item label="文档标题" prop="title">
          <el-input v-model="uploadForm.title" placeholder="请输入文档标题" />
        </el-form-item>

        <el-form-item label="所属目录" prop="parentKey" class="directory-form-item">
          <!-- 目录选择模式切换 -->
          <div class="directory-mode-switch">
            <el-radio-group v-model="uploadForm.dirMode" size="small">
              <el-radio-button value="existing">选择已有目录</el-radio-button>
              <el-radio-button value="new">创建新目录</el-radio-button>
            </el-radio-group>
          </div>

          <!-- 选择已有目录 - 统一使用 el-cascader -->
          <template v-if="uploadForm.dirMode === 'existing'">
            <el-cascader v-model="uploadForm.parentKey" :options="menu" :props="cascaderProps" placeholder="请选择所属目录"
              style="width: 100%" filterable clearable :show-all-levels="true" @change="handleDirChange">
              <template #default="{ data }">
                <div class="custom-cascader-item">
                  <el-icon v-if="data.type === 'dir'" size="16">
                    <Folder />
                  </el-icon>
                  <el-icon v-else-if="data.type === 'md'" size="16">
                    <Document />
                  </el-icon>
                  <el-icon v-else size="16">
                    <Files />
                  </el-icon>
                  <span :class="{ 'dir-item': data.type === 'dir' }">
                    {{ data.label }}
                  </span>
                </div>
              </template>
            </el-cascader>
          </template>

          <!-- 创建新目录 - 统一使用 el-cascader -->
          <template v-if="uploadForm.dirMode === 'new'">
            <div class="new-dir-container">
              <el-cascader v-model="newDirPath" :options="menu" :props="cascaderProps" placeholder="选择父目录（可选）" clearable
                filterable style="width: 100%; margin-bottom: 12px;">
                <template #default="{ data }">
                  <div class="custom-cascader-item">
                    <el-icon v-if="data.type === 'dir'" size="16">
                      <Folder />
                    </el-icon>
                    <el-icon v-else size="16">
                      <Document />
                    </el-icon>
                    <span :class="{ 'dir-item': data.type === 'dir' }">
                      {{ data.label }}
                    </span>
                  </div>
                </template>
              </el-cascader>

              <el-input v-model="newDirName" placeholder="请输入新目录名称" style="width: 100%; margin-bottom: 12px;">
                <template #prepend>新目录名</template>
              </el-input>

              <div class="new-dir-preview" v-if="newDirPath.length > 0 || newDirName">
                <span class="preview-label">预览路径：</span>
                <span class="preview-path">
                  <template v-if="newDirPath.length > 0">
                    {{ getPathFromCascader(newDirPath) }} /
                  </template>
                  <strong>{{ newDirName || '新目录' }}</strong>
                </span>
              </div>
            </div>
          </template>
        </el-form-item>

        <el-form-item label="文档文件" prop="file">
          <el-upload ref="uploadRef" :limit="1" :auto-upload="false" :on-change="handleFileChange"
            :on-exceed="handleExceed" :before-upload="beforeUpload" accept=".md,.pdf" drag>
            <el-icon class="el-icon--upload">
              <UploadFilled />
            </el-icon>
            <div class="el-upload__text">
              拖拽文件到此处或 <em>点击上传</em>
            </div>
            <template #tip>
              <div class="el-upload__tip">
                支持 Markdown(.md) 或 PDF(.pdf) 文件
              </div>
            </template>
          </el-upload>
        </el-form-item>
      </el-form>

      <template #footer>
        <span class="dialog-footer">
          <el-button @click="uploadDialogVisible = false">取消</el-button>
          <el-button type="primary" :loading="uploading" @click="handleUpload">
            上传
          </el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 移动对话框 -->
    <el-dialog v-model="moveDialogVisible" title="移动到" width="400px">
      <el-cascader v-model="moveTargetKey" :options="menu" :props="moveCascaderProps" placeholder="请选择目标目录"
        style="width: 100%" filterable clearable />
      <template #footer>
        <el-button @click="moveDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="moving" @click="handleMoveConfirm">
          确定
        </el-button>
      </template>
    </el-dialog>

    <!-- 右侧内容区 -->
    <div class="doc-content" v-loading="loading">
      <div v-if="currentDocMeta" class="doc-toolbar">
        <div class="doc-title-block">
          <h2>{{ currentDocTitle }}</h2>
          <div v-if="currentDocContributors.length" class="contributors">
            <span class="contributors-label">贡献者</span>
            <div class="contributors-list">
              <el-tooltip
                v-for="person in currentDocContributors"
                :key="person.key"
                :content="person.name"
                placement="bottom"
              >
                <div class="contributor">
                  <el-avatar :size="28" :src="person.avatar">
                    {{ person.name.slice(0, 1).toUpperCase() }}
                  </el-avatar>
                  <span>{{ person.name }}</span>
                </div>
              </el-tooltip>
            </div>
          </div>
        </div>

        <div class="doc-actions">
          <el-button
            v-if="currentType === 'md'"
            :icon="Download"
            size="small"
            @click="handleDownloadMarkdown"
          >
            下载 MD
          </el-button>
          <el-button
            v-if="isManager"
            :icon="Delete"
            type="danger"
            plain
            size="small"
            @click="handleDeleteCurrent"
          >
            删除
          </el-button>
        </div>
      </div>

      <MarkdownViewer v-if="currentType === 'md'" :source="mdContent" />
      <iframe v-else-if="currentType === 'pdf'" :src="pdfUrl" class="pdf-viewer" />
      <div v-else class="empty-content">
        <el-empty description="请选择文档查看" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted, nextTick } from 'vue'
import { useStore } from 'vuex'
import { ElMessage, ElMessageBox } from 'element-plus'
import {
  Menu, Upload, UploadFilled, Edit, FolderOpened, Delete,
  Folder, Document, Files, Download
} from '@element-plus/icons-vue'
import MarkdownViewer from '@/components/MarkdownViewer.vue'
import {
  getSomeOne, getDocMarkdown, getDocPdfUrl, uploadDoc,
  deleteDoc, renameDoc, moveDoc, createDirectory, downloadDocMarkdown
} from '@/api/users'

/* -------------------- state -------------------- */

const menu = ref([])
const loading = ref(false)
const store = useStore()

const currentType = ref('')
const currentDocTitle = ref('')
const currentDocMeta = ref(null)
const mdContent = ref('')
const pdfUrl = ref('')

const userData = computed(() => store.state.userData || {})
const isManager = computed(() => {
  return String(userData.value.webstatus || '').toLowerCase() === 'ssvip'
})

const normalizeContributor = (item, index = 0) => {
  const name = item?.username || item?.userName || item?.name || item?.uploader || item?.operator || ''
  const avatar = item?.avatar || item?.imageUrl || item?.avatarUrl || item?.headUrl || ''

  if (!name) return null

  return {
    key: `${name}-${avatar || index}`,
    name,
    avatar
  }
}

const currentDocContributors = computed(() => {
  const item = currentDocMeta.value
  if (!item) return []

  const rawList = item.contributors || item.uploaders || item.authors || []
  const list = Array.isArray(rawList) ? rawList : [rawList]
  const normalized = list
    .map((person, index) => normalizeContributor(person, index))
    .filter(Boolean)

  const singleUploader = normalizeContributor({
    username: item.uploaderName || item.uploader || item.operator,
    avatar: item.uploaderAvatar || item.avatar || item.imageUrl
  })

  if (singleUploader) {
    normalized.push(singleUploader)
  }

  const unique = new Map()
  normalized.forEach(person => {
    if (!unique.has(person.name)) {
      unique.set(person.name, person)
    }
  })

  return Array.from(unique.values())
})

const treeRef = ref(null)
const drawerVisible = ref(false)

// 响应式判断手机端
const isMobile = ref(window.innerWidth <= 768)

// 右键菜单
const contextMenu = ref({
  visible: false,
  style: { top: '0px', left: '0px' },
  data: null
})

// 上传相关
const uploadDialogVisible = ref(false)
const uploading = ref(false)
const uploadFormRef = ref(null)
const uploadRef = ref(null)
const uploadForm = ref({
  type: '',
  title: '',
  parentKey: [],
  file: null,
  dirMode: 'existing' // 默认选择已有目录
})

// 新目录相关
const newDirName = ref('')
const newDirPath = ref([])

const uploadRules = {
  title: [{ required: true, message: '请输入文档标题', trigger: 'blur' }],
  parentKey: [
    {
      validator: (rule, value, callback) => {
        if (uploadForm.value.dirMode === 'existing' && (!value || value.length === 0)) {
          callback(new Error('请选择所属目录'))
        } else {
          callback()
        }
      },
      trigger: 'change'
    }
  ],
  file: [{ required: true, message: '请选择文件', trigger: 'change' }]
}

const buildFullPath = (nodes, parentPath = '') => {
  nodes.forEach(node => {
    node.fullPath = parentPath
      ? `${parentPath} / ${node.label}`
      : node.label

    if (node.children && node.children.length) {
      buildFullPath(node.children, node.fullPath)
    }
  })
}

// 移动相关
const moveDialogVisible = ref(false)
const moving = ref(false)
const moveTargetKey = ref([])
const currentMoveItem = ref(null)

/* key -> 叶子节点映射 */
const menuMap = new Map()

/* md 缓存 */
const mdCache = new Map()

/* el-tree 字段映射 */
const treeProps = {
  label: 'label',
  children: 'children'
}

// 级联选择器配置
const cascaderProps = {
  expandTrigger: 'hover',
  value: 'key',
  label: 'label',
  children: 'children',
  checkStrictly: true,
  emitPath: true,
  disabled: (data) => data.type !== 'dir' // 只允许选择目录
}

// 移动对话框的级联选择器配置（禁用当前项）
const moveCascaderProps = {
  expandTrigger: 'hover',
  value: 'key',
  label: 'label',
  children: 'children',
  checkStrictly: true,
  emitPath: true,
  disabled: (data) => {
    if (!currentMoveItem.value) return data.type !== 'dir'
    return data.key === currentMoveItem.value.key || data.type !== 'dir'
  }
}

/* -------------------- utils -------------------- */

/* 构建 menuMap（只存 md / pdf） */
const buildMenuMap = (items) => {
  items.forEach(item => {
    if (item.type === 'md' || item.type === 'pdf') {
      menuMap.set(item.key, item)
    }

    if (Array.isArray(item.children) && item.children.length) {
      buildMenuMap(item.children)
    }
  })
}

/* 找到第一个可用文档 */
const findFirstLeafKey = (items) => {
  for (const item of items) {
    if (item.type === 'md' || item.type === 'pdf') {
      return item.key
    }
    if (item.children?.length) {
      const key = findFirstLeafKey(item.children)
      if (key) return key
    }
  }
  return ''
}

/* 根据key查找文档标题 */
const findDocTitleByKey = (key, items) => {
  for (const item of items) {
    if (item.key === key) return item.label
    if (item.children?.length) {
      const title = findDocTitleByKey(key, item.children)
      if (title) return title
    }
  }
  return ''
}

// 从级联选择器获取路径
const getPathFromCascader = (keys) => {
  const findNodePath = (items, targetKeys, currentPath = []) => {
    if (!targetKeys.length) return currentPath.join(' / ')

    const currentKey = targetKeys[0]
    for (const item of items) {
      if (item.key === currentKey) {
        currentPath.push(item.label)
        if (item.children && targetKeys.length > 1) {
          return findNodePath(item.children, targetKeys.slice(1), currentPath)
        }
        return currentPath.join(' / ')
      }
      if (item.children) {
        const result = findNodePath(item.children, targetKeys, [...currentPath])
        if (result) return result
      }
    }
    return currentPath.join(' / ')
  }

  return findNodePath(menu.value, keys) || keys.join(' / ')
}

/* -------------------- api -------------------- */

const sortRootMenu = (list) => {
  const priorityKey = '站点介绍'

  return [...list].sort((a, b) => {
    if (a.label === priorityKey) return -1
    if (b.label === priorityKey) return 1
    return 0
  })
}

const loadMenu = async () => {
  try {
    const response = await getSomeOne('X431 Server', 'apidoc/getmenujson')
    const res = response.data

    if (res.code !== 200 || !Array.isArray(res.data)) {
      throw new Error('菜单数据异常')
    }

    // 排序
    menu.value = sortRootMenu(res.data)

    // ⭐ 构建完整路径
    buildFullPath(menu.value)

    // 构建map
    menuMap.clear()
    buildMenuMap(menu.value)

  } catch (err) {
    console.error(err)
    ElMessage.error(err.message || '菜单加载失败')
  }
}

// 刷新菜单（供父组件调用）
const refreshMenu = () => {
  loadMenu()
}

/* -------------------- doc loader -------------------- */

const loadDoc = async (key) => {
  const item = menuMap.get(key)
  if (!item) return

  loading.value = true
  const requestKey = key

  // 更新当前文档标题
  currentDocTitle.value = findDocTitleByKey(key, menu.value)
  currentDocMeta.value = item

  try {
    if (item.type === 'md') {
      currentType.value = 'md'

      if (mdCache.has(item.path)) {
        mdContent.value = mdCache.get(item.path)
        return
      }

      const res = await getDocMarkdown('X431 Server', item.path)

      if (requestKey !== key) return

      mdCache.set(item.path, res.data)
      mdContent.value = res.data
    }

    else if (item.type === 'pdf') {
      currentType.value = 'pdf'
      pdfUrl.value = getDocPdfUrl('X431 Server', item.path)
    }
  } catch (err) {
    console.error(err)
    ElMessage.error('文档加载失败')
  } finally {
    loading.value = false
  }
}

/* -------------------- 上传相关 -------------------- */

const openUploadDialog = () => {
  uploadDialogVisible.value = true
}

const handleUploadClosed = () => {
  uploadForm.value = {
    type: '',
    title: '',
    parentKey: [],
    file: null,
    dirMode: 'existing'
  }
  newDirName.value = ''
  newDirPath.value = []
  uploadRef.value?.clearFiles()
}

// 文件上传前的校验
const beforeUpload = (file) => {
  const fileName = file.name
  const lastDotIndex = fileName.lastIndexOf('.')
  const fileExt = lastDotIndex > 0 ? fileName.substring(lastDotIndex).toLowerCase() : ''
  
  const isValidExt = fileExt === '.md' || fileExt === '.pdf'
  if (!isValidExt) {
    ElMessage.error('只能上传 .md 或 .pdf 文件！')
  }
  return isValidExt
}

const handleFileChange = (file) => {
  uploadForm.value.file = file.raw
  
  // 获取文件扩展名
  const fileName = file.name
  const lastDotIndex = fileName.lastIndexOf('.')
  const fileExt = lastDotIndex > 0 ? fileName.substring(lastDotIndex).toLowerCase() : ''
  
  // 根据扩展名自动设置文档类型
  if (fileExt === '.md') {
    uploadForm.value.type = 'md'
  } else if (fileExt === '.pdf') {
    uploadForm.value.type = 'pdf'
  } else {
    // 不支持的文件类型
    uploadForm.value.type = ''
    ElMessage.error('不支持的文件类型，请上传 .md 或 .pdf 文件')
    // 清空文件
    uploadRef.value?.clearFiles()
    uploadForm.value.file = null
    return
  }
  
  // 如果文档标题为空，自动使用文件名（不含扩展名）填充
  if (!uploadForm.value.title && file.name) {
    const fileNameWithoutExt = lastDotIndex > 0 ? fileName.substring(0, lastDotIndex) : fileName
    uploadForm.value.title = fileNameWithoutExt
  }
}

const handleExceed = () => {
  ElMessage.warning('只能上传一个文件')
}

const handleDirChange = (value) => {
  if (value && value.length > 0) {
    console.log('选择的目录路径:', getPathFromCascader(value))
  }
}

// 获取最终的目标目录key
const getTargetParentKey = async () => {
  if (uploadForm.value.dirMode === 'existing') {
    if (!uploadForm.value.parentKey || uploadForm.value.parentKey.length === 0) {
      ElMessage.warning('请选择所属目录')
      return null
    }

    // 返回选中的目录 key
    return uploadForm.value.parentKey[uploadForm.value.parentKey.length - 1]
  } 
  else {
    // ===== 新建目录模式 =====
    if (!newDirName.value) {
      ElMessage.warning('请输入新目录名称')
      return null
    }

    // 当前路径
    const basePath = newDirPath.value.length > 0
      ? newDirPath.value[newDirPath.value.length - 1]
      : ''

    // 👉 拼接最终路径（交给后端创建）
    // 例如： "abc123/newFolder"
    const finalPath = basePath
      ? `${basePath}/${newDirName.value}`
      : newDirName.value

    return finalPath
  }
}

const handleUpload = async () => {
  if (!uploadFormRef.value) return
  
  // 检查文件类型
  if (!uploadForm.value.type) {
    ElMessage.error('请选择有效的文档文件（.md 或 .pdf）')
    return
  }

  uploadFormRef.value.validate(async (valid) => {
    if (!valid) return

    const targetParentKey = await getTargetParentKey()
    if (!targetParentKey) return

    console.log('上传文件路径:', targetParentKey, '类型:', uploadForm.value.type)

    uploading.value = true

    const formData = new FormData()
    formData.append('type', uploadForm.value.type)
    formData.append('title', uploadForm.value.title)
    formData.append('parentKey', targetParentKey)
    formData.append('uploaderName', userData.value.username || '')
    formData.append('uploaderAvatar', userData.value.imageUrl || '')
    formData.append('file', uploadForm.value.file)

    try {
      const res = await uploadDoc('X431 Server', formData)

      ElMessage.success('上传成功')
      uploadDialogVisible.value = false

      await loadMenu() // 刷新菜单

      if (!currentType.value) {
        const newNode = res.data || res

        nextTick(() => {
          treeRef.value?.setCurrentKey(newNode.key)
        })

        loadDoc(newNode.key)
      }

    } catch (err) {
      console.error('上传失败:', err)
      ElMessage.error(err.message || '上传失败')
    } finally {
      uploading.value = false
    }
  })
}

/* -------------------- 右键菜单 -------------------- */

const handleContextMenu = (event, data) => {
  // 只对文件节点显示右键菜单
  if (data.type !== 'md' && data.type !== 'pdf') return

  contextMenu.value = {
    visible: true,
    style: {
      top: `${event.clientY}px`,
      left: `${event.clientX}px`
    },
    data
  }

  // 点击其他地方关闭菜单
  document.addEventListener('click', closeContextMenu)
}

const closeContextMenu = () => {
  contextMenu.value.visible = false
  document.removeEventListener('click', closeContextMenu)
}

// 重命名
const handleRename = () => {
  closeContextMenu()
  ElMessageBox.prompt('请输入新名称', '重命名', {
    inputValue: contextMenu.value.data.label,
    inputValidator: (val) => {
      if (!val) return '名称不能为空'
      return true
    }
  }).then(async ({ value }) => {
    try {
      await renameDoc('X431 Server', contextMenu.value.data.key, value)
      ElMessage.success('重命名成功')
      await loadMenu() // 刷新菜单

      // 如果重命名的是当前显示的文档，更新标题
      if (currentDocTitle.value === contextMenu.value.data.label) {
        currentDocTitle.value = value
      }
    } catch (err) {
      ElMessage.error(err.message || '重命名失败')
    }
  }).catch(() => { })
}

// 移动
const handleMove = () => {
  closeContextMenu()
  currentMoveItem.value = contextMenu.value.data
  moveTargetKey.value = []
  moveDialogVisible.value = true
}

// 移动确认
const handleMoveConfirm = async () => {
  if (!moveTargetKey.value || moveTargetKey.value.length === 0) {
    ElMessage.warning('请选择目标目录')
    return
  }

  moving.value = true
  try {
    const targetKey = moveTargetKey.value[moveTargetKey.value.length - 1]
    await moveDoc('X431 Server', currentMoveItem.value.key, targetKey)
    ElMessage.success('移动成功')
    moveDialogVisible.value = false
    await loadMenu() // 刷新菜单
  } catch (err) {
    ElMessage.error(err.message || '移动失败')
  } finally {
    moving.value = false
  }
}

// 删除
const handleDelete = () => {
  closeContextMenu()
  ElMessageBox.confirm(`确定要删除 "${contextMenu.value.data.label}" 吗？`, '警告', {
    type: 'warning'
  }).then(async () => {
    try {
      await deleteDoc('X431 Server', contextMenu.value.data.key)
      ElMessage.success('删除成功')

      // 如果删除的是当前显示的文档，清空内容
      if (currentDocTitle.value === contextMenu.value.data.label) {
        currentType.value = ''
        mdContent.value = ''
        pdfUrl.value = ''
        currentDocTitle.value = ''
        currentDocMeta.value = null
      }

      await loadMenu() // 刷新菜单
    } catch (err) {
      ElMessage.error(err.message || '删除失败')
    }
  }).catch(() => { })
}

const handleDeleteCurrent = () => {
  if (!currentDocMeta.value) return

  contextMenu.value.data = currentDocMeta.value
  handleDelete()
}

const handleDownloadMarkdown = async () => {
  if (!currentDocMeta.value || currentType.value !== 'md') return

  try {
    await downloadDocMarkdown(
      'X431 Server',
      currentDocMeta.value.path,
      `${currentDocTitle.value || currentDocMeta.value.label || 'document'}.md`
    )
  } catch (err) {
    console.error(err)
    ElMessage.error(err.message || 'MD 下载失败')
  }
}

/* -------------------- events -------------------- */

const handleNodeClick = (data) => {
  // 只处理文件节点
  if (data.type !== 'md' && data.type !== 'pdf') return

  loadDoc(data.key)
}

// 移动端节点点击
const handleMobileNodeClick = (data) => {
  if (data.type !== 'md' && data.type !== 'pdf') return

  // 关闭抽屉
  drawerVisible.value = false

  // 加载文档
  loadDoc(data.key)
}

const handleResize = () => {
  isMobile.value = window.innerWidth <= 768
}

/* -------------------- lifecycle -------------------- */

onMounted(() => {
  window.addEventListener('resize', handleResize)
  loadMenu().then(() => {
    const firstKey = findFirstLeafKey(menu.value)
    if (firstKey) {
      nextTick(() => {
        treeRef.value?.setCurrentKey(firstKey)
      })
      loadDoc(firstKey)
    }
  })
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  document.removeEventListener('click', closeContextMenu)
})

// 暴露方法给父组件
defineExpose({
  refreshMenu
})
</script>

<style scoped>
.api-doc-layout {
  display: flex;
  height: 100%;
  overflow: hidden;
}

/* ================= 桌面端 ================= */

.doc-tree-container {
  width: 400px;
  border-right: 1px solid #e5e7eb;
  overflow-y: auto;
  background: #fff;
}

.tree-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid #e5e7eb;
  background-color: #fafafa;
}

.tree-title {
  font-size: 16px;
  font-weight: 600;
}

.doc-tree {
  padding: 10px;
  font-size: 15px;
}

/* 树节点 */

.tree-node {
  display: flex;
  align-items: center;
  width: 100%;
}

.tree-label {
  display: inline-block;
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 不同类型 */

.is-dir {
  font-weight: 600;
  color: #303133;
}

.is-md {
  color: #409eff;
}

.is-pdf {
  color: #e67e22;
}

/* Element 树节点 */

.doc-tree :deep(.el-tree-node__content) {
  height: auto;
  min-height: 34px;
  padding: 4px 8px;
}

.doc-tree :deep(.el-tree-node__content:hover) {
  background: #f5f7fa;
}

/* ================= 文档区 ================= */

.doc-content {
  flex: 1;
  overflow: auto;
  padding: 20px;
  background: #fff;
}

.doc-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 18px;
  padding-bottom: 14px;
  border-bottom: 1px solid #ebeef5;
}

.doc-title-block {
  min-width: 0;
}

.doc-title-block h2 {
  margin: 0 0 10px;
  color: #1f2937;
  font-size: 22px;
  line-height: 1.3;
  font-weight: 700;
}

.contributors {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.contributors-label {
  color: #909399;
  font-size: 13px;
}

.contributors-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.contributor {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 160px;
  padding: 3px 9px 3px 3px;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  background: #f9fafb;
  color: #4b5563;
  font-size: 13px;
}

.contributor span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.doc-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}

.pdf-viewer {
  width: 100%;
  height: calc(100vh - 80px);
  border: none;
}

/* ================= 空内容 ================= */

.empty-content {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

/* ================= 移动端 ================= */

.is-mobile {
  flex-direction: column;
}

.mobile-menu-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px;
  border-bottom: 1px solid #eee;
  background: #fff;
}

.mobile-left {
  display: flex;
  gap: 10px;
}

.mobile-title {
  font-size: 15px;
  font-weight: 600;
}

.drawer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 10px;
}

.drawer-title {
  font-size: 16px;
  font-weight: 600;
}

.mobile-drawer :deep(.el-drawer__body) {
  padding: 0 10px;
}

.mobile-tree {
  font-size: 16px;
}

/* ================= 右键菜单 ================= */

.context-menu {
  position: fixed;
  z-index: 9999;
  width: 160px;
  background: #fff;
  border-radius: 6px;
  border: 1px solid #ebeef5;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 6px 0;
}

.context-menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  cursor: pointer;
  font-size: 14px;
}

.context-menu-item:hover {
  background: #f5f7fa;
}

.context-menu-item.delete {
  color: #f56c6c;
}

/* ================= 上传目录 ================= */

.directory-mode-switch {
  margin-bottom: 10px;
}

.new-dir-container {
  width: 100%;
}

.new-dir-preview {
  font-size: 13px;
  color: #909399;
}

.preview-label {
  margin-right: 6px;
}

.preview-path {
  color: #303133;
}

/* ================= Cascader ================= */

.custom-cascader-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 2px 0;
}

.dir-item {
  font-weight: 500;
}

:deep(.el-cascader-node__label) {
  display: flex;
  align-items: center;
  gap: 4px;
}

/* ================= 类型提示 ================= */

.type-tip {
  margin-left: 8px;
  font-size: 13px;
  color: #909399;
}

@media (max-width: 768px) {
  .doc-toolbar {
    flex-direction: column;
  }

  .doc-actions {
    width: 100%;
    justify-content: flex-start;
  }
}
</style>
